import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Input,
  Popover,
  Text,
} from "@chakra-ui/react";
import { DateObj, useDayzed } from "dayzed";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import { formatDate } from "../helpers/time";

const monthNames = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const weekDays = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

type Props = {
  setValues: (dates: [string, string] | undefined) => void;
  values?: [string, string];
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  isDisabled?: boolean;
};

const DateRangePicker = ({
  placeholder = "Rango de fechas",
  setValues,
  values,
  minDate,
  maxDate,
  isDisabled = false,
}: Props) => {
  const [selected, setSelected] = useState<[string, string] | [string] | []>(
    []
  );
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

  useEffect(() => {
    if (values && values[0] !== "" && values[1] !== "") {
      setSelected(values);
    }
  }, [values]);

  const buildDateRange = ({ date }: DateObj) => {
    if (selected.length !== 1) return setSelected([formatDate(date)]);

    if (date < new Date(`${selected[0]}T00:00:00`)) {
      setSelected([formatDate(date), selected[0]]);
      setValues([formatDate(date), selected[0]]);
    } else {
      setSelected([selected[0], formatDate(date)]);
      setValues([selected[0], formatDate(date)]);
    }

    setIsOpen(false);
  };

  const handleOpenChange = (details: { open: boolean }) => {
    setIsOpen(details.open);
  };

  const { calendars, getBackProps, getForwardProps, getDateProps } = useDayzed({
    selected: selected.map((date) => new Date(`${date}T00:00:00`)),
    onDateSelected: buildDateRange,
    minDate,
    maxDate,
  });

  const calendar = calendars[0];

  const isInRange = (date: Date) => {
    if (selected.length === 1 && hoveredDate) {
      const start = new Date(`${selected[0]}T00:00:00`);
      const end = hoveredDate;
      return (date > start && date <= end) || (date >= end && date < start);
    }
    if (selected.length === 2) {
      const start = new Date(`${selected[0]}T00:00:00`);
      const end = new Date(`${selected[1]}T00:00:00`);
      return date > start && date < end;
    }
    return false;
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Box position="relative" width="100%">
        <Popover.Trigger asChild>
          <Box position="relative">
            <Input
              size="sm"
              placeholder={placeholder}
              _placeholder={{ color: "#5D5C60" }}
              readOnly
              cursor="pointer"
              pl={4}
              pr={10}
              value={
                selected.length === 2 ? selected[0] + " - " + selected[1] : ""
              }
              disabled={isDisabled}
            />
            <Box
              position="absolute"
              right={3}
              top="50%"
              transform="translateY(-50%)"
              zIndex={1}
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) {
                  if (selected.length === 2) {
                    setIsOpen(false);
                    setSelected([]);
                    setValues(undefined);
                  } else {
                    setIsOpen(true);
                  }
                }
              }}
            >
              {selected.length === 2 ? <X size={16} /> : <Calendar size={16} />}
            </Box>
          </Box>
        </Popover.Trigger>

        <Popover.Positioner>
          <Popover.Content p={2} width="auto">
            <Flex justify="space-between" align="center" mb={4}>
              <Button
                {...getBackProps({ calendars })}
                variant="ghost"
                size="sm"
              >
                <ChevronLeft />
              </Button>
              <Text fontWeight="medium">
                {monthNames[calendar.month]} {calendar.year}
              </Text>
              <Button
                {...getForwardProps({ calendars })}
                variant="ghost"
                size="sm"
              >
                <ChevronRight />
              </Button>
            </Flex>
            <Grid templateColumns="repeat(7, 1fr)" gap={1}>
              {weekDays.map((day) => (
                <GridItem
                  key={day}
                  textAlign="center"
                  fontWeight="medium"
                  fontSize="sm"
                >
                  {day}
                </GridItem>
              ))}
              {calendar.weeks.map((week, weekIdx) =>
                week.map((dateObj, idx) => {
                  if (typeof dateObj === "string") {
                    return <GridItem key={`${weekIdx}-${idx}`} />;
                  }

                  const { date, selected, selectable, today } = dateObj;
                  const inRange = isInRange(date);

                  return (
                    <GridItem key={`${weekIdx}-${idx}`}>
                      <Button
                        {...getDateProps({ dateObj })}
                        size="sm"
                        width="100%"
                        height="32px"
                        variant={selected ? "solid" : "ghost"}
                        bg={
                          inRange
                            ? "palomma.500"
                            : selected
                            ? "palomma.500"
                            : today
                            ? "gray.100"
                            : undefined
                        }
                        color={
                          inRange
                            ? "white"
                            : selected
                            ? "white"
                            : today
                            ? "black"
                            : undefined
                        }
                        _hover={{
                          bg: inRange
                            ? "merchants.primary.app"
                            : selected
                            ? "merchants.primary.app"
                            : today
                            ? "gray.100"
                            : undefined,
                        }}
                        opacity={inRange ? 0.5 : 1}
                        borderRadius="md"
                        disabled={!selectable}
                        onMouseEnter={() => setHoveredDate(date)}
                        onMouseLeave={() => setHoveredDate(null)}
                      >
                        {date.getDate()}
                      </Button>
                    </GridItem>
                  );
                })
              )}
            </Grid>
          </Popover.Content>
        </Popover.Positioner>
      </Box>
    </Popover.Root>
  );
};

export default DateRangePicker;
